import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ErrorResponse } from '@core/models';
import { promoCodeActions } from '@store/promocode/promocode.actions';
import { PromoCodeRepository } from '@core/repositories/promocode.repository';
import { userActions } from '@store/user';
import { AnalystService } from '@app/services/analyst/analyst.service';
import { AnalyticsEventsEnum } from '@core/models/analytics.model';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { promoCodeSelectors } from '@store/promocode/promocode.selectors';

@Injectable()
export class PromoCodeEffects {
  constructor(
    private actions$: Actions,
    private promoCodeRepository: PromoCodeRepository,
    private analystService: AnalystService,
    private store: Store,
  ) {}

  public getList = createEffect(() => {
    return this.actions$.pipe(
      ofType(promoCodeActions.getList),
      switchMap(() => this.promoCodeRepository.getList().pipe(
        map((response) => promoCodeActions.getListSuccess({ promoCodes: response.data.promo_codes })),
        catchError((error: unknown) => of(promoCodeActions.getListFailure({ error: error as ErrorResponse })))),
      ),
    );
  });

  public validatePromoCode = createEffect(() => {
    return this.actions$.pipe(
      ofType(promoCodeActions.validate),
      switchMap(({ code }) => this.promoCodeRepository.getValidatePromoCode(code).pipe(
        map((response) => promoCodeActions.validateSuccess({ promoCode: response.data })),
        catchError((error: unknown) => of(promoCodeActions.validateFailure({ error: error as ErrorResponse })))),
      ),
    );
  });

  public applyPromoCode = createEffect(() => {
    return this.actions$.pipe(
      ofType(promoCodeActions.apply),
      switchMap(({ code }) => this.promoCodeRepository.postApplyPromoCode(code).pipe(
        map(() => promoCodeActions.applySuccess()),
        catchError((error: unknown) => of(promoCodeActions.applyFailure({ error: error as ErrorResponse })))),
      ),
    );
  });

  public applySuccess = createEffect(() => {
    return this.actions$.pipe(
      ofType(promoCodeActions.applySuccess),
      map(() => promoCodeActions.getList()),
    );
  });

  public getUserAfterApplyPromoCode = createEffect(() => {
    return this.actions$.pipe(
      ofType(promoCodeActions.applySuccess),
      concatLatestFrom(() => this.store.select(promoCodeSelectors.selectPromoCode)),
      tap(([, promocode]) => {
        this.analystService.logEvent(AnalyticsEventsEnum.PromoCodeApplySuccess, {
          value: promocode?.value ?? promocode?.basic_subscribe_months,
          type: promocode?.basic_subscribe_months ? 'subscribe' : 'wallet',
        }).then();
      }),
      map(() => userActions.getUser()),
    );
  });

  // public getPromoListAfterApplyPromoCode = createEffect(() => {
  //   return this.actions$.pipe(
  //     ofType(promoCodeActions.applySuccess),
  //     map(() => promoCodeActions.getList()),
  //   );
  // });
}